import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tabs, Input, DatePicker, Button, Tag, Space, Alert, Tooltip } from 'antd';
import { DownloadOutlined, ReloadOutlined, PhoneOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { toast } from '../../../utils/toast.js';

// Trang quản lý lead 5 công cụ nhà thuốc — chỉ ADMIN (YC-ADM-08). API: GET /pharmacy-tool/<nguồn>/leads

const fmtDate = (v) => (v ? new Date(v).toLocaleString('vi-VN', { hour12: false }) : '');
const fmtNum = (v, digits = 1) =>
  v === null || v === undefined || v === '' || Number.isNaN(Number(v))
    ? ''
    : Number(v).toLocaleString('vi-VN', { maximumFractionDigits: digits });
const parseJson = (v) => {
  if (!v) return {};
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch {
    return {};
  }
};

const PHAN_LOAI_CTBH = {
  mo_moi: 'Mở mới',
  ngay_hoi: 'Ngày hội',
  tri_an: 'Tri ân',
  upsale: 'Tăng đơn',
  giu_chan: 'Giữ chân',
};

const phoneCell = (phone) =>
  phone ? (
    <a href={`tel:${phone}`} style={{ whiteSpace: 'nowrap' }}>
      <PhoneOutlined /> {phone}
    </a>
  ) : (
    ''
  );

// Mỗi cột: title, get(row) -> giá trị xuất Excel; render tùy chọn cho bảng
const DATE_COL = { key: 'createdAt', title: 'Thời gian', get: (r) => fmtDate(r.createdAt), width: 150 };

const SOURCES = [
  {
    key: 'health-check',
    label: 'Đo sức khỏe',
    fileName: 'lead-do-suc-khoe',
    columns: [
      DATE_COL,
      { key: 'pharmacyName', title: 'Nhà thuốc', get: (r) => r.pharmacyName, width: 180 },
      { key: 'contactName', title: 'Người liên hệ', get: (r) => r.contactName, width: 150 },
      { key: 'phone', title: 'SĐT', get: (r) => r.phone, render: phoneCell, width: 130 },
      { key: 'province', title: 'Tỉnh', get: (r) => r.province, width: 110 },
      { key: 'score', title: 'Điểm', get: (r) => r.score, width: 70 },
      {
        key: 'ranking',
        title: 'Xếp hạng',
        get: (r) => r.ranking,
        render: (v) => (v && v !== 'Chưa có báo cáo' ? <Tag color="green">{v}</Tag> : <span style={{ color: '#4b5d55' }}>{v}</span>),
        width: 150,
      },
      { key: 'portraitName', title: 'Chân dung quầy', get: (r) => r.portraitName, width: 180 },
      { key: 'dt', title: 'Doanh thu tháng (tr)', get: (r) => fmtNum(parseJson(r.metricsData).doanhThuThang), width: 120 },
      { key: 'ln', title: 'Lợi nhuận tháng (tr)', get: (r) => fmtNum(parseJson(r.metricsData).loiNhuanThang), width: 120 },
      { key: 'address', title: 'Địa chỉ', get: (r) => r.address, width: 220, excelOnly: true },
      { key: 'bien', title: 'Biên lãi gộp (%)', get: (r) => fmtNum(parseJson(r.metricsData).bienLaiGop), excelOnly: true },
      { key: 'ton', title: 'Số ngày tồn kho', get: (r) => fmtNum(parseJson(r.metricsData).ngayTon, 0), excelOnly: true },
    ],
  },
  {
    key: 'quiz',
    label: 'Trắc nghiệm',
    fileName: 'lead-trac-nghiem',
    columns: [
      DATE_COL,
      { key: 'fullName', title: 'Họ tên', get: (r) => r.fullName, width: 160 },
      { key: 'phone', title: 'SĐT', get: (r) => r.phone, render: phoneCell, width: 130 },
      { key: 'pharmacyName', title: 'Nhà thuốc', get: (r) => r.pharmacyName, width: 170 },
      { key: 'province', title: 'Tỉnh', get: (r) => r.province, width: 110 },
      { key: 'position', title: 'Vị trí', get: (r) => r.position, width: 130 },
      { key: 'topicTitle', title: 'Chủ đề', get: (r) => r.topicTitle || r.topicKey, width: 200 },
      { key: 'score', title: 'Điểm', get: (r) => fmtNum(r.score), width: 70 },
      { key: 'ranking', title: 'Xếp loại', get: (r) => r.ranking, width: 120 },
    ],
  },
  {
    key: 'salary',
    label: 'Lương thưởng',
    fileName: 'lead-luong-thuong',
    columns: [
      DATE_COL,
      { key: 'name', title: 'Họ tên', get: (r) => r.name, width: 160 },
      { key: 'phone', title: 'SĐT', get: (r) => r.phone, render: phoneCell, width: 130 },
      { key: 'store', title: 'Nhà thuốc', get: (r) => r.store, width: 170 },
      { key: 'doanhThuTrieu', title: 'Doanh thu (tr/tháng)', get: (r) => fmtNum(r.doanhThuTrieu), width: 120 },
      { key: 'soNguoi', title: 'Số người', get: (r) => r.soNguoi, width: 80 },
      { key: 'moHinh', title: 'Mô hình', get: (r) => r.moHinh, width: 90 },
      { key: 'quyLuongTrieu', title: 'Quỹ lương (tr)', get: (r) => fmtNum(r.quyLuongTrieu), width: 110 },
      { key: 'mucRuiRo', title: 'Mức rủi ro', get: (r) => r.mucRuiRo, width: 100 },
      { key: 'bienLaiGop', title: 'Biên lãi gộp (%)', get: (r) => fmtNum(r.bienLaiGop), excelOnly: true },
      { key: 'quyTrenDoanhThu', title: 'Quỹ lương / doanh thu (%)', get: (r) => fmtNum(r.quyTrenDoanhThu), excelOnly: true },
      { key: 'quyTrenLaiGop', title: 'Quỹ lương / lãi gộp (%)', get: (r) => fmtNum(r.quyTrenLaiGop), excelOnly: true },
    ],
  },
  {
    key: 'promotion',
    label: 'Giá nhập thật',
    fileName: 'lead-gia-nhap-that',
    columns: [
      DATE_COL,
      { key: 'name', title: 'Họ tên', get: (r) => r.name, width: 160 },
      { key: 'phone', title: 'SĐT', get: (r) => r.phone, render: phoneCell, width: 130 },
      { key: 'pharmacyName', title: 'Nhà thuốc', get: (r) => r.pharmacyName, width: 170 },
      { key: 'programName', title: 'Chương trình', get: (r) => r.programName, width: 200 },
      { key: 'supplierName', title: 'Nhà cung cấp', get: (r) => r.supplierName, width: 150 },
      { key: 'totalDiscount', title: 'Tổng chiết khấu (%)', get: (r) => fmtNum(r.totalDiscount), width: 120 },
      { key: 'productCount', title: 'Số sản phẩm', get: (r) => r.productCount, width: 90 },
      { key: 'notes', title: 'Ghi chú', get: (r) => r.notes, excelOnly: true },
    ],
  },
  {
    key: 'sales-campaign',
    label: 'Chương trình bán hàng',
    fileName: 'lead-chuong-trinh-ban-hang',
    columns: [
      DATE_COL,
      { key: 'tenQuay', title: 'Nhà thuốc', get: (r) => r.tenQuay, width: 180 },
      { key: 'tenChu', title: 'Chủ quầy', get: (r) => r.tenChu, width: 150 },
      { key: 'dienThoai', title: 'SĐT', get: (r) => r.dienThoai, render: phoneCell, width: 130 },
      { key: 'tinh', title: 'Tỉnh', get: (r) => r.tinh, width: 110 },
      { key: 'maPhanLoai', title: 'Phân loại', get: (r) => PHAN_LOAI_CTBH[r.maPhanLoai] || r.maPhanLoai, width: 110 },
      // Lead chương trình bán hàng: mọi số tiền theo triệu đồng (HT-06 D-06)
      { key: 'doanhSoNgay', title: 'Doanh số ngày (tr)', get: (r) => fmtNum(r.doanhSoNgay, 2), width: 120 },
      { key: 'nganSach', title: 'Ngân sách (tr)', get: (r) => fmtNum(r.nganSach, 2), width: 110 },
      { key: 'chiPhi', title: 'Chi phí (tr)', get: (r) => fmtNum(r.chiPhi, 2), width: 110 },
      { key: 'batDau', title: 'Bắt đầu', get: (r) => r.batDau, width: 100 },
      { key: 'soNgay', title: 'Số ngày', get: (r) => r.soNgay, excelOnly: true },
      { key: 'khuVuc', title: 'Khu vực', get: (r) => r.khuVuc, excelOnly: true },
      { key: 'loaiHinh', title: 'Loại hình', get: (r) => r.loaiHinh, excelOnly: true },
      { key: 'donGiaTB', title: 'Giá trị hóa đơn TB (tr)', get: (r) => fmtNum(r.donGiaTB, 3), excelOnly: true },
    ],
  },
];

const EXPORT_MAX_ROWS = 5000;
const EXPORT_PAGE_SIZE = 100;

export function AdminLeadsPanel() {
  const [activeKey, setActiveKey] = useState(SOURCES[0].key);
  const [keyword, setKeyword] = useState('');
  const [searchText, setSearchText] = useState('');
  const [range, setRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const source = useMemo(() => SOURCES.find((s) => s.key === activeKey), [activeKey]);

  const filters = useMemo(
    () => ({
      q: searchText || undefined,
      from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
      to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
    }),
    [searchText, range]
  );

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await pharmacyToolApi.getToolLeads(activeKey, { ...filters, page, limit: pageSize });
      setData({ items: res?.items || [], total: res?.pagination?.total || 0 });
    } catch (err) {
      // D-05: lỗi CSDL không còn bị giấu thành danh sách rỗng
      setError(err.message || 'Không tải được danh sách lead');
      setData({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [activeKey, filters, page, pageSize]);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await pharmacyToolApi.getLeadCounts({ from: filters.from, to: filters.to });
      setCounts(res || {});
    } catch {
      setCounts({});
    }
  }, [filters.from, filters.to]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const tableColumns = useMemo(
    () =>
      source.columns
        .filter((c) => !c.excelOnly)
        .map((c) => ({
          key: c.key,
          title: c.title,
          width: c.width,
          render: (_, row) => (c.render ? c.render(c.get(row), row) : c.get(row)),
        })),
    [source]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = [];
      let p = 1;
      let total = 0;
      do {
        const res = await pharmacyToolApi.getToolLeads(activeKey, { ...filters, page: p, limit: EXPORT_PAGE_SIZE });
        rows.push(...(res?.items || []));
        total = res?.pagination?.total || 0;
        p += 1;
      } while (rows.length < total && rows.length < EXPORT_MAX_ROWS);

      if (!rows.length) {
        toast.info('Không có lead nào để xuất');
        return;
      }
      const sheetRows = rows.map((r) => Object.fromEntries(source.columns.map((c) => [c.title, c.get(r) ?? ''])));
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      ws['!cols'] = source.columns.map((c) => ({ wch: Math.max(12, Math.round((c.width || 140) / 7)) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lead');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `${source.fileName}-${stamp}.xlsx`);
      if (total > EXPORT_MAX_ROWS) {
        toast.warning(`Chỉ xuất ${EXPORT_MAX_ROWS} lead mới nhất. Hãy lọc theo ngày để xuất phần còn lại.`);
      } else {
        toast.success(`Đã xuất ${rows.length} lead`);
      }
    } catch (err) {
      toast.error(err.message || 'Không xuất được file Excel');
    } finally {
      setExporting(false);
    }
  };

  const resetPageAnd = (fn) => (value) => {
    setPage(1);
    fn(value);
  };

  return (
    <section className="adm-panel">
      <div className="adm-panel-bar">
        <span className="adm-panel-meta">Khách để lại thông tin khi dùng 5 công cụ nhà thuốc</span>
      </div>
      <div className="adm-panel-pad" style={{ paddingTop: 4 }}>
        <Tabs
          activeKey={activeKey}
          onChange={resetPageAnd(setActiveKey)}
          items={SOURCES.map((s) => ({
            key: s.key,
            label: (
              <span>
                {s.label}{' '}
                {counts[s.key] !== undefined && (
                  <Tag style={{ marginInlineStart: 4 }} color={s.key === activeKey ? 'green' : 'default'}>
                    {counts[s.key]}
                  </Tag>
                )}
              </span>
            ),
          }))}
        />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <Input.Search
            allowClear
            placeholder="Tìm theo tên, SĐT, nhà thuốc"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={(v) => {
              setPage(1);
              setSearchText(v.trim());
            }}
            style={{ width: 280, maxWidth: '100%' }}
          />
          <DatePicker.RangePicker
            value={range}
            onChange={resetPageAnd(setRange)}
            format="DD/MM/YYYY"
            placeholder={['Từ ngày', 'Đến ngày']}
            style={{ maxWidth: '100%' }}
          />
          <Space style={{ marginLeft: 'auto' }} wrap>
            <Tooltip title="Tải lại">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchLeads();
                  fetchCounts();
                }}
              />
            </Tooltip>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
              style={{ background: '#005537', borderColor: '#005537' }}
            >
              Xuất Excel
            </Button>
          </Space>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Không tải được danh sách lead"
            description={error}
          />
        )}

        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          columns={tableColumns}
          dataSource={data.items}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total: data.total,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (t) => `${t} lead`,
            onChange: (p, ps) => {
              setPage(ps !== pageSize ? 1 : p);
              setPageSize(ps);
            },
          }}
          locale={{ emptyText: error ? 'Có lỗi khi tải dữ liệu' : 'Chưa có lead nào' }}
        />
      </div>
    </section>
  );
}
